import { useSearchParams as ReactUseSearchParams } from 'react-router-dom'

/**
 * Custom hook to manage URL search parameters.
 *
 * @template T - A record type representing the search parameters.
 * @returns An object containing the current search parameters and functions to manipulate them.
 */
export const useSearchParams = <T extends Record<string, string>>() => {
  const [params, setSearchParams] = ReactUseSearchParams()
  const searchParams = {} as { [K in keyof T]: string | undefined }

  for (const key of params.keys()) {
    searchParams[key as keyof T] = params.get(key) || undefined
  }

  /**
   * Updates the search parameters with the provided new parameters.
   *
   * @param newParams - An object containing the new search parameters to set.
   */
  const updateSearchParams = (newParams: Partial<T>) => {
    const updatedParams = new URLSearchParams(params)
    Object.entries(newParams).forEach(([key, value]) => {
      if (value === undefined) updatedParams.delete(key)
      else updatedParams.set(key, value as string)
    })
    setSearchParams(updatedParams)
  }

  /**
   * Adds new search parameters to the existing ones.
   *
   * @param newParams - An object containing the new search parameters to add.
   */
  const addSearchParams = (newParams: Partial<T>) => {
    const updatedParams = new URLSearchParams(params)
    Object.entries(newParams).forEach(([key, value]) => {
      if (value !== undefined) updatedParams.append(key, value as string)
    })
    setSearchParams(updatedParams)
  }

  /**
   * Removes the specified search parameters.
   *
   * @param keys - An array of keys representing the search parameters to remove.
   */
  const removeSearchParams = (keys: (keyof T)[]) => {
    const updatedParams = new URLSearchParams(params)
    keys.forEach((key) => updatedParams.delete(key as string))
    setSearchParams(updatedParams)
  }

  return { searchParams, setSearchParams, updateSearchParams, addSearchParams, removeSearchParams }
}
